'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClockCounterClockwise,
  X,
  Spinner,
  User,
  FileText,
  GitCommit,
  ArrowRight,
} from '@phosphor-icons/react';
import { useToastActions } from '../ui/Toast';
import { workflowApi } from '../../services/api';
import type { WorkflowChangeLog, ChangeType } from '../../types';

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getChangeTypeIcon(changeType: ChangeType) {
  switch (changeType) {
    case 'node_added':
      return <span className="w-2 h-2 rounded-full bg-emerald-500" />;
    case 'node_deleted':
      return <span className="w-2 h-2 rounded-full bg-red-500" />;
    case 'node_modified':
      return <span className="w-2 h-2 rounded-full bg-amber-500" />;
    case 'node_moved':
      return <span className="w-2 h-2 rounded-full bg-blue-500" />;
    case 'edge_added':
      return <span className="w-2 h-2 rounded-full bg-violet-500" />;
    case 'edge_deleted':
      return <span className="w-2 h-2 rounded-full bg-rose-500" />;
    case 'edge_modified':
      return <span className="w-2 h-2 rounded-full bg-orange-500" />;
    case 'rollback':
      return <span className="w-2 h-2 rounded-full bg-cyan-500" />;
    default:
      return <span className="w-2 h-2 rounded-full bg-zinc-400" />;
  }
}

interface ChangeHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
}

export function ChangeHistoryDrawer({
  isOpen,
  onClose,
  workflowId,
  workflowName,
}: ChangeHistoryDrawerProps) {
  const { t } = useTranslation();
  const toast = useToastActions();

  const [logs, setLogs] = useState<WorkflowChangeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<WorkflowChangeLog | null>(null);

  useEffect(() => {
    if (!isOpen || !workflowId) return;
    setLoading(true);
    workflowApi
      .getWorkflowChangeLogs(workflowId)
      .then((res) => {
        const raw = res as { data?: { logs?: WorkflowChangeLog[]; total?: number } };
        const list = raw?.data?.logs;
        setLogs(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        toast.error(t('workflow.loadChangeLogsFailed'));
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, workflowId, t, toast]);

  const renderChangeDetail = (log: WorkflowChangeLog) => {
    const oldValue = log.oldValue ? (() => {
      try {
        return JSON.parse(log.oldValue);
      } catch {
        return log.oldValue;
      }
    })() : null;
    const newValue = log.newValue ? (() => {
      try {
        return JSON.parse(log.newValue);
      } catch {
        return log.newValue;
      }
    })() : null;

    return (
      <div className="mt-3 space-y-2">
        {log.nodeType && (
          <p className="text-xs text-zinc-500">
            {t('workflow.nodeType')}: <span className="font-medium text-zinc-700">{log.nodeType}</span>
          </p>
        )}
        {log.field && (
          <p className="text-xs text-zinc-500">
            {t('workflow.field')}: <span className="font-medium text-zinc-700">{log.field}</span>
          </p>
        )}
        {oldValue !== null && newValue !== null && (
          <div className="mt-2 p-2 bg-zinc-50 rounded-lg border border-zinc-100">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-zinc-500">{t('workflow.oldValue')}:</span>
              <span className="text-red-600 line-through truncate max-w-[120px]">
                {typeof oldValue === 'object' ? JSON.stringify(oldValue) : String(oldValue)}
              </span>
              <ArrowRight className="w-3 h-3 text-zinc-400" />
              <span className="text-zinc-500">{t('workflow.newValue')}:</span>
              <span className="text-emerald-600 truncate max-w-[120px]">
                {typeof newValue === 'object' ? JSON.stringify(newValue) : String(newValue)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="change-history-drawer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex"
      >
        <div
          className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden
        />
        <motion.aside
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                <GitCommit className="w-5 h-5 text-violet-600" weight="fill" />
              </div>
              <div>
                <h2 className="font-semibold text-zinc-900">{t('workflow.changeHistory')}</h2>
                <p className="text-xs text-zinc-500 truncate max-w-[200px]">{workflowName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="w-6 h-6 text-zinc-400 animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8">
                <ClockCounterClockwise className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-sm text-zinc-500">{t('workflow.noChangeLogs')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-xl border p-4 transition-all cursor-pointer ${
                      selectedLog?.id === log.id
                        ? 'bg-violet-50 border-violet-200'
                        : 'bg-white border-zinc-200 hover:border-zinc-300'
                    }`}
                    onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">{getChangeTypeIcon(log.changeType)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-900">
                            {t(`workflow.changeTypes.${log.changeType}`)}
                          </p>
                          <span className="text-xs text-zinc-400">
                            {formatDate(log.createdAt)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                          {log.changedByName && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {log.changedByName}
                            </span>
                          )}
                          {log.nodeId && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {log.nodeId}
                            </span>
                          )}
                        </div>

                        {selectedLog?.id === log.id && renderChangeDetail(log)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.aside>
      </motion.div>
    </AnimatePresence>
  );
}
